import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createPemserterPriceListUsd,
  deletePemserterPriceListUsd,
  getPemserterPriceListUsd,
  listPemserterPriceListUsd,
  updatePemserterPriceListUsd,
  type CreatePemserterPriceListUsdBody,
  type ListPemserterPriceListUsdParams,
  type PaginatedPemserterPriceListUsd,
  type PemserterPriceListUsd,
  type UpdatePemserterPriceListUsdBody,
} from "../api/pemserterPriceListUsd";

const KEY = "pemserter-price-list-usd";

export function usePemserterPriceListUsd(params: ListPemserterPriceListUsdParams) {
  return useQuery<PaginatedPemserterPriceListUsd>({
    queryKey: [KEY, "list", params],
    queryFn: () => listPemserterPriceListUsd(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function usePemserterPriceListUsdDetail(rowId: number | null) {
  return useQuery<PemserterPriceListUsd>({
    queryKey: [KEY, "detail", rowId],
    queryFn: () => getPemserterPriceListUsd(rowId as number),
    enabled: rowId !== null,
    staleTime: 30_000,
  });
}

export function useCreatePemserterPriceListUsd() {
  const queryClient = useQueryClient();
  return useMutation<PemserterPriceListUsd, Error, CreatePemserterPriceListUsdBody>({
    mutationFn: (body) => createPemserterPriceListUsd(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useUpdatePemserterPriceListUsd() {
  const queryClient = useQueryClient();
  return useMutation<
    PemserterPriceListUsd,
    Error,
    { rowId: number; body: UpdatePemserterPriceListUsdBody }
  >({
    mutationFn: ({ rowId, body }) => updatePemserterPriceListUsd(rowId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

/** Soft-delete via DELETE /v1/pemserter-price-list-usd/:id. */
export function useDeletePemserterPriceListUsd() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (rowId) => deletePemserterPriceListUsd(rowId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
